import {
  Pagination as HeroPagination,
  PaginationProps as HeroPaginationProps,
} from "@heroui/react";
import { useControlledState } from "@react-stately/utils";
import clsx from "clsx";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { useEffect, useMemo } from "react";
import { useTranslations } from "translations";
import { Select } from "../select/select";
import { SelectProps } from "../select/utils";

/**
 * Pagination component that provides page navigation and rows per page selection.
 * Supports both controlled and uncontrolled modes.
 */
export type PaginationProps = Omit<
  HeroPaginationProps,
  "children" | "showShadow" | "isDisabled"
> & {
  isCentered?: boolean;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  page?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  selectVariant?: SelectProps<{ id: string; label: string }>["variant"];
  showRowsPerPage?: boolean;
  total?: number;
};

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
const commonClasses = (isCompact: boolean | undefined) =>
  clsx(
    "text-gray-900 shadow-none",
    "forced-colors:border-[ButtonText]",
    "forced-colors:text-[ButtonText]",
    "forced-colors:data-[focus=true]:outline-solid!",
    "forced-colors:data-[focus=true]:outline-2!",
    "forced-colors:data-[focus=true]:outline-[ButtonText]!",
    {
      "bg-gray-100 data-[focus=true]:-outline-offset-2": isCompact,
      "border border-gray-500 bg-white data-[focus=true]:outline-offset-1":
        !isCompact,
      "data-[focus=true]:data-[active=true]:outline-offset-1": isCompact,
      "forced-colors:bg-[GrayText]!": isCompact,
    },
  );

function usePagination({
  onPageChange,
  onRowsPerPageChange,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  total,
}: {
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  page?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  total?: number;
}) {
  const [page, setPage] = useControlledState(controlledPage, 1, onPageChange);

  const [rowsPerPage, setRowsPerPage] = useControlledState(
    controlledRowsPerPage,
    DEFAULT_ROWS_PER_PAGE,
    onRowsPerPageChange,
  );

  const totalPages = useMemo(() => {
    if (total === undefined || total <= 0) return 0;
    return Math.ceil(total / rowsPerPage);
  }, [total, rowsPerPage]);

  // Ensure page is within valid range
  const validPage = useMemo(() => {
    if (totalPages === 0) return 1;
    return Math.max(1, Math.min(page, totalPages));
  }, [page, totalPages]);

  return {
    page: validPage,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    totalPages,
  };
}

export function Pagination({
  isCentered,
  onPageChange,
  onRowsPerPageChange,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  selectVariant = "horizontal",
  showRowsPerPage = true,
  total,
  ...props
}: PaginationProps) {
  const translations = useTranslations();
  const { page, rowsPerPage, setPage, setRowsPerPage, totalPages } =
    usePagination({
      onPageChange,
      onRowsPerPageChange,
      page: controlledPage,
      rowsPerPage: controlledRowsPerPage,
      total,
    });

  const { updateA11yMessage } = useA11yMessage();

  useEffect(() => {
    if (total && totalPages > 0) {
      updateA11yMessage(
        translations.accessibility.paginationCount({
          page: `${page}`,
          totalPages: `${totalPages}`,
        }),
      );
    }
  }, [page, totalPages, total, updateA11yMessage]);

  const selectItems = useMemo(
    () =>
      rowsPerPageOptions.map((value) => ({
        id: String(value),
        label: String(value),
      })),
    [rowsPerPageOptions],
  );

  const renderRowsPerPageSelector = () => {
    if (!showRowsPerPage) return null;

    return (
      <div className="flex min-w-[180px] items-center gap-2">
        <Select
          items={selectItems}
          label={translations.predict.dashboard.pagination}
          renderItem={(item) => item.label}
          selectedKeys={[String(rowsPerPage)]}
          size="sm"
          variant={selectVariant}
          onSelectionChange={(keys) => {
            if (keys) {
              const selectedKey = Array.from(keys)[0];
              const newRowsPerPage = Number(selectedKey);
              setRowsPerPage(newRowsPerPage);
            }
          }}
        />
      </div>
    );
  };

  if (total === undefined || total <= 0 || totalPages === 0) return null;

  return (
    <div
      className={clsx("flex flex-col items-center tablet:flex-row", {
        "justify-between gap-4": !isCentered,
        "justify-center gap-6": isCentered,
      })}
    >
      {renderRowsPerPageSelector()}
      <HeroPagination
        {...props}
        classNames={{
          chevronNext: clsx("text-gray-900", "forced-colors:text-[ButtonText]"),
          cursor: clsx(
            "shadow-none",
            "forced-colors:bg-[SelectedItem]",
            "forced-colors:text-[SelectedItemText]",
            "forced-colors:border-[SelectedItemText]",
          ),
          ellipsis: clsx("text-gray-900", "forced-colors:text-[ButtonText]"),
          forwardIcon: clsx("text-gray-500", "forced-colors:text-[ButtonText]"),
          item: commonClasses(props.isCompact),
          next: clsx(
            commonClasses(props.isCompact),
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            "forced-colors:data-[disabled=true]:text-[GrayText]",
            "forced-colors:data-[disabled=true]:border-[GrayText]",
          ),
          prev: clsx(
            commonClasses(props.isCompact),
            "data-[disabled=true]:bg-gray-200 data-[disabled=true]:text-gray-900",
            "forced-colors:data-[disabled=true]:text-[GrayText]",
            "forced-colors:data-[disabled=true]:border-[GrayText]",
          ),
          wrapper: clsx(
            "shadow-none",
            "forced-colors:bg-[Canvas]",
            {
              "bg-gray-400": props.isCompact,
              "forced-colors:bg-[GrayText]!": props.isCompact,
            },
            {
              "": !props.isCompact,
              "forced-colors:bg-[Canvas]!": !props.isCompact,
            },
          ),
        }}
        page={page}
        total={totalPages}
        onChange={setPage}
      />
    </div>
  );
}

Pagination.displayName = "RecareUI.Pagination";
